import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../core/data/redux/store";
import {
  fetchCompanies,
  fetchLeadsForms,
  fetchLeadsQueueData,
  assignLeads,
} from "../../../core/data/redux/leads-queue/thunks";
import {
  selectActiveCompany,
  selectQueueFormId,
  selectPageLoading,
  selectLeadForms,
} from "../../../core/data/redux/leads-queue/selectors";
import LeadsQueueHeader from "./components/leads-in-queue/LeadsQueueHeader";
import LeadsQueueFilters from "./components/leads-in-queue/LeadsQueueFilters";
import LeadsQueueTable from "./components/leads-in-queue/LeadsQueueTable";
import Loader from "../../../core/common/loader";
import LeadsQueueFooter from "./components/leads-in-queue/LeadsQueueFooter";
import { DEFAULT_FILTER } from "../../../core/data/redux/leads-queue/constants";
import type { DateRange, SourceFilter, SourceHierarchy, Lead, Pagination, Employee } from "../../../core/data/redux/leads-queue/types";
import { exportQueueData } from "../../../core/data/redux/services/leadsQueueApi";
import { notify } from "../../../services/toast.config";
import { useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";

const DEFAULT_DATE_RANGE: DateRange = { start_date: null, end_date: null };
const DEFAULT_PAGINATION: Pagination = { total: 0, current_page: 1, per_page: 30, total_pages: 0 };

const LeadsQueue: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const activeCompany = useSelector(selectActiveCompany);
  const formId = useSelector(selectQueueFormId);
  const leadForms = useSelector(selectLeadForms);
  const pageLoading = useSelector(selectPageLoading);
  const [tableLoading, setTableLoading] = useState(false);
  const [isExportStarted, setIsExportStarted] = useState(false);
  const navigate = useNavigate();
  // Local State
  const [activeSourceType, setActiveSourceType] = useState<string>("all");
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>(DEFAULT_FILTER);
  const [dateRange, setDateRange] = useState<DateRange>(DEFAULT_DATE_RANGE);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [bulkAssignUserId, setBulkAssignUserId] = useState<string | null>(null);

  // Data State
  const [leads, setLeads] = useState<Lead[]>([]);
  const [pagination, setPagination] = useState<Pagination>(DEFAULT_PAGINATION);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [sourceHierarchy, setSourceHierarchy] = useState<SourceHierarchy | null>(null);

  useEffect(() => { dispatch(fetchCompanies()); }, [dispatch]);

  useEffect(() => {
    if (!activeCompany.company_id) return;
    setActiveSourceType("all");
    setSourceFilter(DEFAULT_FILTER);
    setDateRange(DEFAULT_DATE_RANGE);
    setSelectedRowKeys([]);
    setLeads([]);
    dispatch(fetchLeadsForms({ companyId: activeCompany.company_id, companyType: activeCompany.company_type }));
  }, [activeCompany.company_id, activeCompany.company_type]);

  const loadData = async (
    page = 1,
    limit = 30,
    sourceTypeOverride?: string,
    filterOverride?: SourceFilter,
    dateOverride?: DateRange,
    searchOverride?: string
  ) => {
    if (!activeCompany.company_id || !formId) return;

    setTableLoading(true);
    try {
      const data = await dispatch(fetchLeadsQueueData({
        companyId: activeCompany.company_id,
        companyType: activeCompany.company_type,
        sourceType: sourceTypeOverride ?? activeSourceType,
        sourceFilter: filterOverride ?? sourceFilter,
        dateRange: dateOverride ?? dateRange,
        formId,
        page,
        limit,
        search: searchOverride !== undefined ? searchOverride : searchQuery
      })).unwrap();

      setLeads(data.leads);
      setPagination(data.pagination);
      setEmployees(data.employees || []);
      // Hierarchy & Counts are now in the SAME response!
      setSourceHierarchy({
        facebook: data.hierarchy?.facebook || [],
        whatsapp: data.hierarchy?.whatsapp || [],
        public_form: data.hierarchy?.public_form || [],
        crm: { count: data.counts?.crm || 0 },
        unknown: { count: data.counts?.unknown || 0 },
        counts: data.counts
      });
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    if (activeCompany.company_id && formId) {
      loadData(1, 30);
    }
  }, [activeCompany.company_id, activeCompany.company_type, formId]);

  const handleReset = () => {
    setActiveSourceType("all");
    setSourceFilter(DEFAULT_FILTER);
    setDateRange(DEFAULT_DATE_RANGE);
    setSearchQuery("");
    loadData(1, 30, "all", DEFAULT_FILTER, DEFAULT_DATE_RANGE, "");
  };

  const handleBulkAssign = async () => {
    if (!bulkAssignUserId || selectedRowKeys.length === 0) return;
    try {
      await dispatch(assignLeads({
        leadIds: selectedRowKeys,
        userId: bulkAssignUserId,
        companyId: activeCompany.company_id,
        companyType: activeCompany.company_type,
      })).unwrap();
      setSelectedRowKeys([]);
      setBulkAssignUserId(null);
      loadData(pagination.current_page, pagination.per_page);
    } catch (error) { }
  };

  const handleExport = async () => {
    try {
      notify.success("Download Started")
      setIsExportStarted(true)
      const signature = localStorage.getItem("signature");
      const blob = await exportQueueData(
        signature,
        activeCompany.company_id,
        activeCompany.company_type,
        activeSourceType,
        sourceFilter,
        dateRange,
        formId,
        searchQuery
      );

      // Create download link dynamically
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Leads_Queue_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Export failed", error);
      notify.error("Download Failed")
    } finally {
      setIsExportStarted(false)

    }
  };

  return (
    <>
      {pageLoading && <Loader />}
      <div className="page-wrapper">
        <div className="content">
          <LeadsQueueHeader isExportStarted={isExportStarted} handleExport={handleExport} activeView="queue" />

          <LeadsQueueFilters
                activeCompany={activeCompany}
                formId={formId}
                activeSourceType={activeSourceType}
                sourceTypeCounts={sourceHierarchy?.counts ?? null}
                sourceHierarchy={sourceHierarchy}
                sourceFilter={sourceFilter}
                dateRange={dateRange}
                selectedRowKeys={selectedRowKeys}
                bulkAssignUserId={bulkAssignUserId}
                employees={employees}
                leadForms={leadForms}
                isLoading={tableLoading}
                displayTotalCount={pagination.total}
                onSourceTypeChange={(key) => {
                  setActiveSourceType(key);
                  setSourceFilter(DEFAULT_FILTER);
                  loadData(1, 30, key, DEFAULT_FILTER);
                }}
                onSourceFilterChange={(filter) => {
                  setSourceFilter(filter);
                  loadData(1, 30, undefined, filter);
                }}
                onDateRangeChange={(dr) => {
                  setDateRange(dr);
                  loadData(1, 30, undefined, undefined, dr);
                }}
                onSearch={(term) => {
                  setSearchQuery(term);
                  loadData(1, 30, undefined, undefined, undefined, term);
                }}
                onReset={handleReset}
                onBulkAssignUserChange={setBulkAssignUserId}
                onBulkAssign={handleBulkAssign}
              />
              <LeadsQueueTable
                leads={leads}
                pagination={pagination}
                tableLoading={tableLoading}
                selectedRowKeys={selectedRowKeys}
                onSelectedRowKeysChange={setSelectedRowKeys}
                onPageChange={(page) => loadData(page, pagination.per_page)}
              />
        </div>
        <LeadsQueueFooter />
      </div>
    </>
  );
};
export default LeadsQueue;
